/**
 * Shared types, constants, and pure helpers used by both UsersPage and
 * UserManager.
 */
import type React from 'react';
import { AccountType, AccessTypeAnalytics, User } from '../../types/auth';
import { userApi } from '../../services/api/userApi';

// ---------------------------------------------------------------------------
// Shared interface
// ---------------------------------------------------------------------------

export interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  accountType: AccountType;
  companyId: number;

  // Additional fields
  branch?: string;
  subCompany?: string; // For CompanyAdmin and BranchAdmin
  subCustomerId?: number; // For SubCustomerAdmin
  mail?: string;
  accessTypeAnalytics?: AccessTypeAnalytics;
  dataStorageKey?: string;
  passKey?: string;
}

// ---------------------------------------------------------------------------
// Default form state factory
// ---------------------------------------------------------------------------

export const makeDefaultFormData = (companyId: number): CreateUserData => ({
  firstName: '',
  lastName: '',
  email: '',
  accountType: AccountType.SubCustomerAdmin,
  companyId,
  branch: '',
  subCompany: '',
  mail: '',
  accessTypeAnalytics: AccessTypeAnalytics.Passive,
  dataStorageKey: '',
  passKey: '',
});

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Validates a single form field and returns an error message (or '' when valid).
 * The `editingUser` flag is only relevant for the (currently commented-out)
 * password field kept here for completeness.
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

const validateOptionalEmail = (value: string): string =>
  value && value.length > 0 && !EMAIL_REGEX.test(value)
    ? 'Please enter a valid business email address'
    : '';

const validatePassword = (value: string, editingUser: boolean): string => {
  if (!editingUser && value.length < 8) return 'Password must be at least 8 characters';
  if (value.length > 0 && value.length < 8) return 'Password must be at least 8 characters';
  return '';
};

export const validateFormField = (
  name: string,
  value: string,
  editingUser: boolean,
): string => {
  switch (name) {
    case 'firstName':
      return value.length < 2 ? 'First name must be at least 2 characters' : '';
    case 'lastName':
      return value.length < 2 ? 'Last name must be at least 2 characters' : '';
    case 'email':
      return EMAIL_REGEX.test(value) ? '' : 'Please enter a valid email address';
    case 'mail':
      return validateOptionalEmail(value);
    case 'password':
      return validatePassword(value, editingUser);
    default:
      return '';
  }
};

// ---------------------------------------------------------------------------
// Key generators
// ---------------------------------------------------------------------------

export const generateDataStorageKey = (): string =>
  Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join('');

export const generatePassKey = (): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: 32 }, () =>
    characters.charAt(Math.floor(Math.random() * characters.length)),
  ).join('');
};

// ---------------------------------------------------------------------------
// RBAC submit-time validation
// ---------------------------------------------------------------------------

/**
 * Validates RBAC role-specific fields in the form data and merges any errors
 * into the provided errors object (mutates in place).
 *
 * @param subCompanyAdminLabel - The display label used for the SubCompanyAdmin
 *   role in the error message (differs between UsersPage and UserManager).
 */
export const validateRbacFields = (
  formData: Pick<CreateUserData, 'accountType' | 'subCompany' | 'branch' | 'subCustomerId'>,
  errors: Record<string, string>,
  subCompanyAdminLabel = 'Company Admin',
): void => {
  if (formData.accountType === AccountType.SubCompanyAdmin) {
    if (!formData.subCompany?.trim()) {
      errors.subCompany = `Sub-company is required for ${subCompanyAdminLabel} role`;
    }
  }

  if (formData.accountType === AccountType.BranchAdmin) {
    if (!formData.subCompany?.trim()) {
      errors.subCompany = 'Sub-company is required for Branch Admin role';
    }
    if (!formData.branch?.trim()) {
      errors.branch = 'Branch is required for Branch Admin role';
    }
  }

  if (formData.accountType === AccountType.SubCustomerAdmin) {
    if (!formData.subCustomerId) {
      errors.subCustomerId = 'Sub-customer is required for SubCustomer Admin role';
    }
  }
};

/**
 * Apply the always-required user-form field validations (first name, last name,
 * email). Mutates the passed `errors` object.
 */
export function validateRequiredUserFields(
  formData: CreateUserData,
  errors: Record<string, string>,
): void {
  if (!formData.firstName.trim()) errors.firstName = 'First name is required';
  if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
  if (!formData.email.trim()) errors.email = 'Email is required';
}

// ---------------------------------------------------------------------------
// Shared form-change handlers
// ---------------------------------------------------------------------------

/**
 * Build the shared `handleInputChange` handler used by the user-form screens.
 * Updates a single form field and re-validates string values.
 */
export function makeHandleInputChange(
  formData: CreateUserData,
  setFormData: (data: CreateUserData) => void,
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  editingUser: User | null,
) {
  return (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
    if (typeof value === 'string') {
      const errorMsg = validateFormField(field, value, !!editingUser);
      setValidationErrors(prev => ({ ...prev, [field]: errorMsg }));
    }
  };
}

/**
 * Build the shared `onSubCompanyChange` / `onBranchChange` / `onSubCustomerChange`
 * handlers passed to <UserFormDialog>.
 */
export function makeUserFormChangeHandlers(
  setSelectedSubCompanyId: (id: number | null) => void,
  setSelectedBranchId: (id: number | null) => void,
  setSelectedSubCustomerId: (id: number | null) => void,
  handleInputChange: (field: string, value: any) => void,
) {
  return {
    onSubCompanyChange: (subCompanyId: number, code: string) => {
      setSelectedSubCompanyId(subCompanyId);
      handleInputChange('subCompany', code);
    },
    onBranchChange: (branchId: number, code: string) => {
      setSelectedBranchId(branchId);
      handleInputChange('branch', code);
    },
    onSubCustomerChange: (subCustomerId: number) => {
      setSelectedSubCustomerId(subCustomerId);
      handleInputChange('subCustomerId', subCustomerId);
    },
  };
}

// ---------------------------------------------------------------------------
// Status toggle
// ---------------------------------------------------------------------------

/**
 * Toggle a user's active status via the API and return the resulting list with
 * the updated user, plus the corresponding success message. Throws on failure
 * so callers can apply their own error handling.
 */
export async function applyUserStatusToggle(
  user: User,
  users: User[],
): Promise<{ users: User[]; successMessage: string }> {
  const updatedUser = user.isActive
    ? await userApi.deactivate(user.id)
    : await userApi.activate(user.id);

  return {
    users: users.map(u => u.id === user.id ? updatedUser : u),
    successMessage: `User ${user.isActive ? 'deactivated' : 'activated'} successfully`,
  };
}

// ---------------------------------------------------------------------------
// Re-exported types (so callers can import from one place)
// ---------------------------------------------------------------------------

export type { SubCompany, Branch, SubCustomer } from '../../types/company';
